import { BrowserAuthError, BrowserAuthErrorMessage, InteractionRequiredAuthError } from "@azure/msal-browser";
import type { AccountInfo, AuthenticationResult, IPublicClientApplication } from "@azure/msal-browser";
import { signInScopes } from "../authConfig";

// The API allows 5 minutes of clock skew; renew an ID token with less than this left.
export const ID_TOKEN_MIN_REMAINING_MS = 5 * 60 * 1000;

// sessionStorage (per tab): set while a sign-in redirect for interaction is under way. Interaction
// needed again in the same tab shows the error instead of redirecting again, so a policy the
// redirect can't satisfy never loops. Cleared when a token comes through.
export const SIGN_IN_REDIRECT_KEY = "docubird-portal-signin-redirect";

// token: send it to GetAuthToken. redirect: the page is leaving for login.microsoftonline.com and
// comes back. error: no token; Home offers "Try again".
export type PortalIdToken =
    | { kind: "token"; idToken: string }
    | { kind: "redirect" }
    | { kind: "error" };

const hasFreshIdToken = (result: AuthenticationResult | null | undefined, nowMs: number): boolean => {
    const exp = (result?.idTokenClaims as { exp?: number } | undefined)?.exp;
    return !!result?.idToken && typeof exp === "number" && exp * 1000 - nowMs >= ID_TOKEN_MIN_REMAINING_MS;
};

// Renewal only a top-level visit to login.microsoftonline.com can finish: an expired refresh token
// whose hidden-iframe fallback gets no session cookie (Safari, third-party cookies blocked), a
// Conditional Access sign-in frequency, a revoked session.
const needsInteraction = (e: unknown): boolean =>
    e instanceof InteractionRequiredAuthError
    || (e instanceof BrowserAuthError && e.errorCode === BrowserAuthErrorMessage.monitorIframeTimeoutError.code);

// No sessionStorage means no loop guard, so it counts as "redirected before": no redirect then.
const redirectedBefore = (): boolean => {
    try {
        return sessionStorage.getItem(SIGN_IN_REDIRECT_KEY) !== null;
    } catch (e) {
        return true;
    }
};

const markRedirect = (redirecting: boolean): void => {
    try {
        if (redirecting) {
            sessionStorage.setItem(SIGN_IN_REDIRECT_KEY, "1");
        } else {
            sessionStorage.removeItem(SIGN_IN_REDIRECT_KEY);
        }
    } catch (e) {
        // Without sessionStorage redirectedBefore() is true, so nothing can loop.
    }
};

const redirectForInteraction = async (
    instance: Pick<IPublicClientApplication, "acquireTokenRedirect">,
    account: AccountInfo
): Promise<PortalIdToken> => {
    if (redirectedBefore()) {
        return { kind: "error" };
    }

    markRedirect(true);
    try {
        await instance.acquireTokenRedirect({ scopes: signInScopes, account });
    } catch (e) {
        // MsalAuthenticationTemplate may already be redirecting for its own login: the page is
        // leaving either way. Any other failure is shown, and a later attempt may redirect again.
        if (!(e instanceof BrowserAuthError && e.errorCode === BrowserAuthErrorMessage.interactionInProgress.code)) {
            markRedirect(false);
            console.error(e);
            return { kind: "error" };
        }
    }
    return { kind: "redirect" };
};

/** "Try again" in a tab whose sign-in redirect came back still needing the user: the next attempt
 *  may go through Microsoft once more (audit 2026-10-19: the flag outlived the reload). */
export const clearPortalRedirectAttempt = (): void => markRedirect(false);

/**
 * The Portal's Entra ID token for api/portal/Auth/GetAuthToken (spec §1.2). msal-browser 2.x
 * returns the cached ID token while the access token from the same response is still valid, even
 * when the ID token itself has expired, so an ID token close to expiry is renewed silently with
 * forceRefresh. When renewal needs the user, the browser goes through login.microsoftonline.com
 * (acquireTokenRedirect, usually without a prompt), never through logoutRedirect, which would end
 * the user's Microsoft session in every app.
 */
export const acquirePortalIdToken = async (
    instance: Pick<IPublicClientApplication, "acquireTokenSilent" | "acquireTokenRedirect">,
    account: AccountInfo | undefined,
    now: () => number = Date.now,
    options: { forceRefresh?: boolean } = {}
): Promise<PortalIdToken> => {
    if (!account) {
        return { kind: "error" };
    }

    try {
        // forceRefresh: the API found the last ID token expired, whatever this clock says.
        const cached: AuthenticationResult | undefined = options.forceRefresh
            ? undefined
            : await instance.acquireTokenSilent({ scopes: signInScopes, account });
        // The API judges a renewed token's lifetime itself; a skewed client clock must not block sign-in.
        const idToken: string | undefined = cached && hasFreshIdToken(cached, now())
            ? cached.idToken
            : (await instance.acquireTokenSilent({ scopes: signInScopes, account, forceRefresh: true }))?.idToken;
        if (!idToken) {
            return { kind: "error" };
        }

        // The redirect attempt stays until the API accepts a token (signInToPortal): one it finds
        // expired may need Microsoft again, and must not redirect on every load (review 2026-10-20).
        return { kind: "token", idToken };
    } catch (e) {
        if (!needsInteraction(e)) {
            console.error(e);
            return { kind: "error" };
        }
    }

    return redirectForInteraction(instance, account);
};
